import { type ZendeskUsersResponse, type ZendeskUser } from "@interfaces/user";
import { zafClient, getCurrentRequesterEmail } from "./zendesk";
import { isCNPJ } from "@utils/validateCNPJ";

// Função utilitária para remover a máscara do CPF/CNPJ
const removeMask = (value: string): string => value.replace(/\D/g, "");

// Função utilitária para aplicar máscara ao CPF
const applyCpfMask = (cpf: string): string => {
  const clean = removeMask(cpf);
  if (clean.length !== 11) return cpf;
  return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
};

// Função utilitária para aplicar máscara ao CNPJ
const applyCnpjMask = (cnpj: string): string => {
  const clean = removeMask(cnpj);
  if (clean.length !== 14) return cnpj;
  return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

// 1️⃣ Busca usuário por CPF (cpf_3) ou CNPJ (cnpj_regex) — tenta com e sem máscara
const findUserByDocument = async (document: string): Promise<ZendeskUser | null> => {
  const raw = removeMask(document);
  const isCnpj = raw.length === 14;
  const field = isCnpj ? "cnpj_regex" : "cpf_3";
  const masked = isCnpj ? applyCnpjMask(raw) : applyCpfMask(raw);

  const searchOne = async (value: string): Promise<ZendeskUser | null> => {
    try {
      const response = (await zafClient.request({
        type: "GET",
        url: `/api/v2/users/search.json?query=${field}:${encodeURIComponent(value)}`,
      })) as ZendeskUsersResponse;
      return response.users?.[0] ?? null;
    } catch (error) {
      console.error(`Erro ao buscar usuário por ${field} (${value}):`, error);
      return null;
    }
  };

  // Busca sem máscara primeiro, depois com máscara
  return (await searchOne(raw)) ?? (await searchOne(masked));
};

// 2️⃣ Busca usuário por email fake determinístico
// const findUserByEmail = async (email: string): Promise<ZendeskUser | null> => {
//   try {
//     const response = (await zafClient.request({
//       type: "GET",
//       url: `/api/v2/users/search.json?query=email:${encodeURIComponent(email)}`,
//     })) as ZendeskUsersResponse;

//     return response.users?.[0] ?? null;
//   } catch (error) {
//     console.error("Erro ao buscar usuário por email:", error);
//     return null;
//   }
// };

// 3️⃣ Busca usuário por external_id
const findUserByExternalId = async (externalId: string): Promise<ZendeskUser | null> => {
  try {
    const response = (await zafClient.request({
      type: "GET",
      url: `/api/v2/users/search.json?external_id=${encodeURIComponent(externalId)}`,
    })) as ZendeskUsersResponse;

    return response.users?.[0] ?? null;
  } catch (error) {
    console.error(`Erro ao buscar usuário por external_id (${externalId}):`, error);
    return null;
  }
};

// 4️⃣ Busca ou cria usuário
// Ordem: external_id (cpf) → external_id (cnpj) → cpf_3 (s/m) → cnpj_regex (s/m) → email → criar
export const getOrCreateUser = async (
  name: string,
  email?: string,
  customFields?: Record<string, any>
): Promise<ZendeskUser> => {
  const cpfRaw = customFields?.cpf_3 ? removeMask(customFields.cpf_3) : "";
  const cnpjRaw = customFields?.cnpj_regex ? removeMask(customFields.cnpj_regex) : "";

  const document = cpfRaw || cnpjRaw;

  if (!document) {
    throw new Error("CPF ou CNPJ do responsável não identificado na base da API do Telecontrol");
  }

  if (cnpjRaw && !isCNPJ(cnpjRaw)) {
    throw new Error("CNPJ inválido. Verifique os dados informados.");
  }

  // const fakeEmail = cpfRaw ? `cpf_${cpfRaw}@email.com` : `cnpj_${cnpjRaw}@email.com`;

  const finalUserFields: Record<string, any> = {
    ...customFields,
    ...(cpfRaw ? { cpf_3: cpfRaw } : { cnpj_regex: cnpjRaw }),
  };

  // Remove campos undefined/nulos/vazios para não sobrescrever dados existentes
  Object.keys(finalUserFields).forEach((key) => {
    const val = finalUserFields[key];
    if (val === undefined || val === null || val === "") {
      delete finalUserFields[key];
    }
  });

  // Campos de endereço que só devem ser enviados se o usuário ainda não tiver um valor no Zendesk,
  // para a OS não sobrescrever um endereço já corrigido/atualizado manualmente
  const ADDRESS_PROTECTED_FIELDS = [
    "endereco_2",
    "_pessoa_fisica__endereco_de_entrega",
    "_pessoa_fisica__bairro_de_cobranca",
    "_pessoa_fisica__bairro_de_entrega",
    "cep__endereco",
  ];

  const updateExistingUser = async (existingUser: ZendeskUser): Promise<ZendeskUser> => {
    const existingFields = (existingUser.user_fields ?? {}) as Record<string, unknown>;
    const fieldsToUpdate = { ...finalUserFields };
    ADDRESS_PROTECTED_FIELDS.forEach((key) => {
      if (existingFields[key]) delete fieldsToUpdate[key];
    });

    const resp = (await zafClient.request({
      type: "PUT",
      url: `/api/v2/users/${existingUser.id}.json`,
      contentType: "application/json",
      data: JSON.stringify({
        user: {
          name,
          external_id: cpfRaw || cnpjRaw,
          user_fields: fieldsToUpdate,
        },
      }),
    })) as { user: ZendeskUser };

    await zafClient.request({
      type: "POST",
      url: `/api/v2/users/${existingUser.id}/tags.json`,
      contentType: "application/json",
      data: JSON.stringify({ tags: ["app-telecontrol"] }),
    });

    return resp.user;
  };

  // 1️⃣ Busca por external_id com CPF
  if (cpfRaw) {
    const userByExternalCpf = await findUserByExternalId(cpfRaw);
    if (userByExternalCpf) return updateExistingUser(userByExternalCpf);
  }

  // 2️⃣ Busca por external_id com CNPJ (garantia extra)
  if (cnpjRaw) {
    const userByExternalCnpj = await findUserByExternalId(cnpjRaw);
    if (userByExternalCnpj) return updateExistingUser(userByExternalCnpj);
  }

  // 3️⃣ Busca por CPF no campo cpf_3 (sem e com máscara)
  if (cpfRaw) {
    const userByCpf = await findUserByDocument(cpfRaw);
    if (userByCpf) return updateExistingUser(userByCpf);
  }

  // 4️⃣ Busca por CNPJ no campo cnpj_regex (sem e com máscara)
  if (cnpjRaw) {
    const userByCnpj = await findUserByDocument(cnpjRaw);
    if (userByCnpj) return updateExistingUser(userByCnpj);
  }

  // 5️⃣ Busca por email fake
  // const userByEmail = await findUserByEmail();
  // if (userByEmail) return updateExistingUser(userByEmail.id);

  // 5️⃣ Busca por email para verificar se já existe
  const findUserByEmail = async (emailValue: string): Promise<ZendeskUser | null> => {
    try {
      const response = (await zafClient.request({
        type: "GET",
        url: `/api/v2/users/search.json?query=email:${encodeURIComponent(emailValue)}`,
      })) as ZendeskUsersResponse;
      return response.users?.[0] ?? null;
    } catch (error) {
      console.error("Erro ao buscar usuário por email:", error);
      return null;
    }
  };

  // 6️⃣ Verifica se o email já existe antes de criar
  const BLOCKED_EMAIL = "naotem@naotem.com.br";
  let emailExists = false;

  if (email && email.toLowerCase() !== BLOCKED_EMAIL) {
    const existingUserWithEmail = await findUserByEmail(email);
    if (existingUserWithEmail) {
      emailExists = true;
      const existingCpf = existingUserWithEmail.user_fields?.cpf_3;
      const existingCnpj = existingUserWithEmail.user_fields?.cnpj_regex;

      if (existingCpf === cpfRaw || existingCnpj === cnpjRaw) {
        return updateExistingUser(existingUserWithEmail);
      }
    }
  }

  // 7️⃣ Nenhum usuário encontrado — cria novo
  const userData: Record<string, any> = {
    name,
    external_id: cpfRaw || cnpjRaw,
    user_fields: finalUserFields,
    tags: ["app-telecontrol"],
  };

  if (email && email.toLowerCase() !== BLOCKED_EMAIL && !emailExists) {
    userData.email = email;
  }

  const response = (await zafClient.request({
    type: "POST",
    url: "/api/v2/users.json",
    contentType: "application/json",
    data: JSON.stringify({
      user: userData,
    }),
  })) as { user: ZendeskUser };

  return response.user;
};

export const returnCurrentUserData = async () => {
  const email = await getCurrentRequesterEmail();

  const response = (await zafClient.request({
    type: "GET",
    url: `/api/v2/users/search.json?query=email:${encodeURIComponent(email)}`,
    contentType: "application/json",
  })) as ZendeskUsersResponse;

  if (response.users.length === 0) return null;

  const user: ZendeskUser = response.users[0];

  return {
    name: user.name,
    email: user.email,
    id: user.id,
    user_fields: user.user_fields,
  };
};
